import { Grid, TextField, Typography, useTheme } from '@mui/material';
import { AccessKeys } from 'app/appConstants';
import { MprcIconButtonDropdown } from 'components';
import { MprcFilterBox, MprcFilterLine } from 'components/filter';
import { IconButtonClearSearch } from 'components/iconButtons/IconButtonClearSearch';
import { IconSort } from 'components/icons';
import { FeedQuestionQueryOrderBy } from 'generated/CoreApi_v1';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////////////////////

export enum PublicationType {
    PublicationType = 0,
    trainingType = 1,
}

export interface IFeedQuestionFilterOptions {
    searchText: string | undefined;
    orderBy: FeedQuestionQueryOrderBy;
}


interface IProps {
    answerCount: number;
    filterOptions: IFeedQuestionFilterOptions;
    onFilterChange: (filter: IFeedQuestionFilterOptions) => void;
}


////////////////////////////////////////////////////////////////////////////////////////////////


export const FeedQuestionFilter: React.FC<IProps> = (props) => {

    const { filterOptions, onFilterChange, answerCount } = props;
    const { t } = useTranslation("knowledge");
    const theme = useTheme();

    const [searchText, setSearchText] = useState<string>(filterOptions.searchText ?? "");

    const inputRef = useRef<HTMLInputElement | undefined>(undefined);

    ///
    const handleButtonCancel = () => {
        setSearchText("");
        onFilterChange({ ...filterOptions, searchText: "" });
    };

    return (
        <MprcFilterBox>
            <MprcFilterLine>
                <div>
                    <Typography variant="h3">{t("Feed.Question.AnswerCountText", { count: answerCount })}</Typography>
                </div>

                <div>
                    <MprcIconButtonDropdown
                        menuItemCheck
                        icon={<IconSort />}
                        title={t("shared:Filter.OrderBy")}
                        selected={filterOptions.orderBy}
                        onChange={(orderBy) => onFilterChange({ ...filterOptions, orderBy })}
                        items={
                            [
                                { text: t("Feed.Question.OrderBy.BestRated"), value: FeedQuestionQueryOrderBy.BestRated },
                                { text: t("Feed.Question.OrderBy.MostRecent"), value: FeedQuestionQueryOrderBy.MostRecent },
                            ]
                        }
                    />
                </div>

            </MprcFilterLine>

            <Grid container>
                <Grid size="grow">
                    <TextField
                        fullWidth
                        placeholder={t("Search.TextPlaceholder", { ns: 'common' })}
                        autoComplete="off"
                        color="warning"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => { if (e.keyCode == 13) onFilterChange({ ...filterOptions, searchText }); }}
                        inputRef={inputRef}
                        accessKey={AccessKeys.Search}
                        sx={{
                            '& .MuiInput-underline:before': { borderBottomColor: theme.palette["warning"].main },
                            input: {
                                '&::placeholder': {
                                    color: theme.palette["warning"].main,
                                    opacity: 1,
                                },
                            }
                        }}
                        slotProps={{
                            input: {
                                endAdornment: searchText.length > 0 &&
                                    <IconButtonClearSearch
                                        size="small"
                                        color="warning"
                                        onClick={() => {
                                            setSearchText("");
                                            handleButtonCancel();
                                            inputRef.current?.focus();
                                        }}
                                    />,
                                onFocus: () => {
                                    if (inputRef)
                                        (inputRef.current as HTMLInputElement).placeholder = t("Feed.Question.SearchTextPlaceholderFocus");
                                },
                                onBlur: () => {
                                    if (inputRef)
                                        (inputRef.current as HTMLInputElement).placeholder = t("Feed.Question.SearchTextPlaceholder");
                                },
                                placeholder: t("Feed.Question.SearchTextPlaceholder")
                            }
                        }}
                    />
                </Grid>
            </Grid>

        </MprcFilterBox>
    );
};
